import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import ContactSection from "@/components/ContactSection";
import HeyflowBanner from "@/components/HeyflowBanner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  MapPin, 
  CheckCircle, 
  Users, 
  Calculator, 
  FileText, 
  Shield,
  TrendingUp,
  Home,
  Euro,
  Clock,
  Key,
  CreditCard,
  Phone
} from "lucide-react";
import janineHardiImage from "@/assets/janine-hardi.jpg";
import seniorenfinanzierungImage from "@/assets/seniorenfinanzierung.png";

const Seniorenfinanzierung = () => {
  const advantages = [
    {
      icon: CreditCard,
      title: "Flexible Auszahlung",
      description: "Senioren entscheiden selbst, ob sie eine Einmalzahlung, eine monatliche Zusatzrente oder eine Mischform wünschen."
    },
    {
      icon: Clock,
      title: "Keine Tilgung zu Lebzeiten",
      description: "Die Rückzahlung erfolgt erst nach dem Tod des Kreditnehmers oder wenn die Immobilie von den Erben übernommen wird."
    },
    {
      icon: Home,
      title: "Eigentum bleibt erhalten",
      description: "Im Gegensatz zu anderen Verrentungsmodellen bleibt die Immobilie vollständig im Besitz der Eigentümer."
    },
    {
      icon: Users,
      title: "Rückzahlung bei Tod oder durch Erben",
      description: "Die Erben können entscheiden: Kredit ablösen oder Immobilie verkaufen."
    },
    {
      icon: Shield,
      title: "Diskrete Lösung",
      description: "Nach außen bleibt alles unverändert – keine Änderungen im Grundbuch hinsichtlich Wohnrechten oder Teilverkäufen."
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Kostenlose Beratung",
      description: "Zunächst klären die Experten, ob eine Seniorenfinanzierung oder ein alternatives Modell besser passt."
    },
    {
      step: "2", 
      title: "Immobilienbewertung",
      description: "Die Immobilie wird bewertet, um die mögliche Kredithöhe festzulegen."
    },
    {
      step: "3",
      title: "Kreditauszahlung", 
      description: "Je nach Wunsch erfolgt die Auszahlung als Einmalbetrag, monatliche Zusatzrente oder Kombination."
    },
    {
      step: "4",
      title: "Keine monatliche Belastung",
      description: "Während der Laufzeit zahlen Senioren keine Raten – lediglich die Zinsen werden dem Kredit hinzugerechnet."
    },
    {
      step: "5",
      title: "Rückzahlung",
      description: "Erfolgt durch den Verkauf der Immobilie oder durch Übernahme/Ablösung durch die Erben."
    }
  ];

  const examples = [
    {
      title: "Einfamilienhaus in Hamburg",
      value: "800.000 €",
      payout: "200.000 € Einmalzahlung",
      benefit: "Keine monatliche Tilgung, Rückzahlung erfolgt beim Verkauf nach dem Tod"
    },
    {
      title: "Eigentumswohnung in München", 
      value: "600.000 €",
      payout: "1.000 € monatlich als Zusatzrente",
      benefit: "Laufzeit bis zum Tod, Rückzahlung durch Erben"
    },
    {
      title: "Haus in Köln",
      value: "500.000 €", 
      payout: "100.000 € + 500 € monatlich",
      benefit: "Erben übernehmen Kredit, Immobilie bleibt in der Familie"
    }
  ];

  const faqs = [
    {
      question: "Wie hoch kann die Auszahlung sein?",
      answer: "Sie richtet sich nach dem Immobilienwert, dem Alter und der Beleihungsgrenze."
    },
    {
      question: "Muss ich Zinsen zahlen?",
      answer: "Die Zinsen werden in der Regel dem Kredit hinzugerechnet und erst am Ende fällig."
    },
    {
      question: "Können meine Erben die Immobilie behalten?", 
      answer: "Ja, die Erben können den Kredit ablösen und Eigentümer bleiben."
    },
    {
      question: "Ist die Immobilie verpfändet?",
      answer: "Ja, sie dient als Sicherheit, bleibt aber im Besitz des Eigentümers."
    },
    {
      question: "Kann ich die Finanzierung kündigen?",
      answer: "Ja, durch vorzeitige Rückzahlung oder Verkauf der Immobilie."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="min-h-screen">
        {/* Breadcrumb */}
        <nav className="container-comfortable py-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary transition-colors">Startseite</Link>
          <span className="mx-2">/</span>
          <span className="text-primary">Seniorenfinanzierung</span>
        </nav>
      
      <Helmet>
        <title>Seniorenfinanzierung: Kredit ohne monatliche Raten | eigenheimverrentung.de</title>
        <meta 
          name="description" 
          content="Seniorenfinanzierung: Kredit gegen Haus als Sicherheit ohne monatliche Raten. Eigentum behalten & Kapital freisetzen. Kostenlose Beratung zur Eigenheimverrentung." 
        />
        <meta name="keywords" content="seniorenfinanzierung, kredit im alter, haus als sicherheit, kapital aus der immobilie, eigentum behalten, auszahlung haus rentner, eigenheimverrentung" />
        <link rel="canonical" href="https://eigenheimverrentung.de/seniorenfinanzierung" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Seniorenfinanzierung: Kredit ohne monatliche Raten" />
        <meta property="og:description" content="Kredit gegen Immobilie als Sicherheit ohne monatliche Raten. Eigentum behalten und trotzdem Kapital freisetzen." />
        <meta property="og:url" content="https://eigenheimverrentung.de/seniorenfinanzierung" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Seniorenfinanzierung: Kredit ohne monatliche Raten" />
        <meta name="twitter:description" content="Kredit gegen Immobilie als Sicherheit ohne monatliche Raten. Eigentum behalten und Kapital freisetzen." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Seniorenfinanzierung – Kredit ohne monatliche Raten",
            "description": "Kompletter Ratgeber zur Seniorenfinanzierung: Kredit gegen Haus als Sicherheit ohne monatliche Raten, Eigentum behalten.",
            "author": {
              "@type": "Organization",
              "name": "eigenheimverrentung.de"
            },
            "publisher": {
              "@type": "Organization",
              "name": "eigenheimverrentung.de",
              "url": "https://eigenheimverrentung.de"
            },
            "datePublished": new Date().toISOString(),
            "dateModified": new Date().toISOString(),
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://eigenheimverrentung.de/seniorenfinanzierung"
            }
          })}
        </script>
        
        {/* FAQ Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Wie hoch kann die Auszahlung sein?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sie richtet sich nach dem Immobilienwert, dem Alter und der Beleihungsgrenze."
                }
              },
              {
                "@type": "Question",
                "name": "Muss ich Zinsen zahlen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Die Zinsen werden in der Regel dem Kredit hinzugerechnet und erst am Ende fällig."
                }
              },
              {
                "@type": "Question",
                "name": "Können meine Erben die Immobilie behalten?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, die Erben können den Kredit ablösen und Eigentümer bleiben."
                }
              },
              {
                "@type": "Question",
                "name": "Ist die Immobilie verpfändet?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, sie dient als Sicherheit, bleibt aber im Besitz des Eigentümers."
                }
              },
              {
                "@type": "Question",
                "name": "Kann ich die Finanzierung kündigen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, durch vorzeitige Rückzahlung oder Verkauf der Immobilie."
                }
              }
            ]
          })}
        </script>

        {/* Organization & Local Business Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "RentePlusImmobilie - Eigenheimverrentung",
            "url": "https://eigenheimverrentung.de",
            "telephone": "+49-89-628246446",
            "email": "info@renteplusimmobilie.de",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "DE"
            },
            "areaServed": "Deutschland",
            "serviceType": ["Leibrente", "Wohnrecht", "Nießbrauchrecht", "Teilverkauf", "Rückmiete", "Seniorenfinanzierung"],
            "description": "Kostenlose Beratung zur Eigenheimverrentung mit Seniorenfinanzierung - Kredit ohne monatliche Raten."
          })}
        </script>

        {/* BreadcrumbList Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Startseite",
                "item": "https://eigenheimverrentung.de/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Seniorenfinanzierung",
                "item": "https://eigenheimverrentung.de/seniorenfinanzierung"
              }
            ]
          })}
        </script>

        {/* HowTo Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Wie beantrage ich eine Seniorenfinanzierung?",
            "description": "Schritt-für-Schritt Anleitung zur Seniorenfinanzierung - Kredit ohne monatliche Raten",
            "totalTime": "P21D",
            "step": processSteps.map((step, index) => ({
              "@type": "HowToStep",
              "position": index + 1,
              "name": step.title,
              "text": step.description
            }))
          })}
        </script>

        {/* Service Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Seniorenfinanzierung",
            "name": "Seniorenfinanzierung - Kredit ohne monatliche Raten",
            "description": "Kredit gegen Immobilie als Sicherheit ohne monatliche Tilgungsraten. Eigentum bleibt vollständig erhalten.",
            "provider": {
              "@type": "LocalBusiness",
              "name": "RentePlusImmobilie - Eigenheimverrentung",
              "url": "https://eigenheimverrentung.de",
              "telephone": "+49-89-628246446",
              "email": "info@renteplusimmobilie.de"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Deutschland"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Senioren die Eigentum behalten und trotzdem Kapital freisetzen möchten"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Seniorenfinanzierung Leistungen",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Keine monatliche Tilgung",
                    "description": "Rückzahlung erst nach dem Tod oder durch die Erben"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Flexible Auszahlung",
                    "description": "Einmalzahlung, monatliche Zusatzrente oder Kombination"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Eigentum bleibt erhalten",
                    "description": "Immobilie bleibt vollständig im Besitz des Eigentümers"
                  }
                }
              ]
            },
            "url": "https://eigenheimverrentung.de/seniorenfinanzierung"
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        <div className="container-comfortable relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="secondary" className="mb-6 text-lg px-6 py-2">
              <MapPin className="mr-2 h-5 w-5" />
              Eigentum erhalten
            </Badge>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-primary mb-6 text-balance">
              Seniorenfinanzierung – Kostenlose Beratung
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 text-balance max-w-3xl mx-auto">
              Kredit gegen Immobilie als Sicherheit ohne monatliche Raten
            </p>

            <p className="text-lg text-muted-foreground mb-12 max-w-4xl mx-auto leading-relaxed">
              Viele Senioren haben ein gemeinsames Problem: Das Vermögen steckt größtenteils in der Immobilie, während die monatliche Rente oft knapp bemessen ist. Gleichzeitig möchte man das Eigenheim nicht verkaufen, sondern weiterhin Eigentümer bleiben. Eine Lösung bietet die Seniorenfinanzierung.
            </p>

            <div className="flex flex-col items-center gap-4">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-xl px-12 py-6">
                  <Calculator className="mr-2 h-6 w-6" />
                  Kostenlose Beratung
                </Button>
                <Button variant="outline" size="lg" className="text-xl px-12 py-6">
                  <FileText className="mr-2 h-6 w-6" />
                  Mehr erfahren
                </Button>
              </div>

              <button 
                className="flex items-center gap-2 text-primary hover:text-primary-hover transition-colors cursor-pointer whitespace-nowrap text-lg font-medium"
                onClick={() => window.open('tel:089628246446', '_self')}
              >
                <Phone className="h-5 w-5" />
                <span>089 62 82 46 46</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-12 md:py-16">
        <div className="container-comfortable">
          <div className="max-w-2xl mx-auto text-center">
            <img 
              src={seniorenfinanzierungImage} 
              alt="Seniorenfinanzierung - Keine monatliche Rückzahlung" 
              className="w-full max-w-md mx-auto rounded-xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Was ist Seniorenfinanzierung */}
      <section className="py-16 md:py-24">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
              Was ist Seniorenfinanzierung?
            </h2>
            
            <Card className="card-elevated mb-8">
              <CardContent className="p-8">
                <p className="text-lg mb-6 text-muted-foreground leading-relaxed">
                  Seniorenfinanzierung ist eine besondere Form des Immobilienkredits, bei dem die Immobilie als Sicherheit dient.
                </p>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Der Kreditnehmer erhält eine flexible Auszahlung: entweder als Einmalbetrag, monatliche Rente oder Kombination.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Während der Laufzeit fallen keine Tilgungsraten an.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Die Rückzahlung erfolgt erst, wenn der Kreditnehmer verstirbt oder wenn die Immobilie veräußert wird.</span>
                  </div>
                </div>

                <div className="bg-accent/10 p-6 rounded-lg">
                  <h4 className="font-semibold text-primary mb-3">Abgrenzung zu anderen Modellen</h4>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p><strong>Leibrente:</strong> Verkauf mit monatlichen Rentenzahlungen – Seniorenfinanzierung dagegen ist ein Kredit, die Immobilie bleibt Eigentum.</p>
                    <p><strong>Teilverkauf:</strong> Eigentum wird geteilt – bei der Seniorenfinanzierung bleibt alles im Besitz des Eigentümers.</p>
                    <p><strong>Rückmiete:</strong> Immobilie wird verkauft – hier nicht.</p>
                    <p><strong>Nießbrauch/Wohnrecht:</strong> Eigentum geht an andere über – Seniorenfinanzierung erhält das Eigentum vollständig.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Vorteile */}
      <section className="py-16 md:py-24 bg-accent/5">
        <div className="container-comfortable">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
            Vorteile der Seniorenfinanzierung
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {advantages.map((advantage, index) => (
              <Card key={index} className="card-elevated h-full">
                <CardHeader className="text-center">
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <advantage.icon className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl text-primary">
                    {advantage.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base leading-relaxed">
                    {advantage.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Für wen geeignet */}
      <section className="py-16 md:py-24">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
              Für wen eignet sich Seniorenfinanzierung?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Clock className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Senioren mit temporärem Liquiditätsbedarf</h3>
                      <p className="text-muted-foreground">z. B. Pflege, Umbau, Reisen.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Home className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Eigentümer ohne Verkaufswunsch</h3>
                      <p className="text-muted-foreground">Die ihr Haus oder ihre Wohnung nicht verkaufen wollen.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Users className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Familien mit Sicherheitsbedarf</h3>
                      <p className="text-muted-foreground">Die das Eigentum sichern, aber gleichzeitig Liquidität schaffen möchten.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Shield className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Menschen mit Erben</h3>
                      <p className="text-muted-foreground">Die später den Kredit übernehmen oder die Immobilie verkaufen können.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="py-16 md:py-24 bg-accent/5">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
              Der Ablauf bei renteplusimmobilie.de
            </h2>

            <div className="space-y-6">
              {processSteps.map((step, index) => (
                <Card key={index} className="card-elevated">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-6">
                      <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg flex-shrink-0">
                        {step.step}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-primary mb-2">{step.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Beispielrechnungen */}
      <section className="py-16 md:py-24">
        <div className="container-comfortable">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
            Beispielrechnungen & Praxisfälle
          </h2>

          <div className="grid lg:grid-cols-3 gap-8">
            {examples.map((example, index) => (
              <Card key={index} className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-xl text-primary">{example.title}</CardTitle>
                  <div className="text-2xl font-bold text-accent">{example.value}</div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-muted-foreground mb-1">Auszahlung:</p>
                    <p className="font-semibold text-lg">{example.payout}</p>
                  </div>
                  <div className="pt-4 border-t border-border">
                    <p className="text-sm font-medium text-primary">→ {example.benefit}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Unterschiede zu anderen Modellen */}
      <section className="py-16 md:py-24 bg-accent/5">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
              Unterschiede zu anderen Verrentungsmodellen
            </h2>

            <div className="space-y-6">
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Clock className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Seniorenfinanzierung vs. Leibrente</h3>
                      <p className="text-muted-foreground">Eigentum bleibt erhalten, Rückzahlung erst später.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Seniorenfinanzierung vs. Teilverkauf</h3>
                      <p className="text-muted-foreground">Keine Abgabe von Eigentumsanteilen.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Home className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Seniorenfinanzierung vs. Rückmiete</h3>
                      <p className="text-muted-foreground">Keine Mietkosten, da man Eigentümer bleibt.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Key className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Seniorenfinanzierung vs. Nießbrauch/Wohnrecht</h3>
                      <p className="text-muted-foreground">Immobilie bleibt komplett im Besitz, keine Übertragung an andere Personen.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
              Häufige Fragen zur Seniorenfinanzierung (FAQ)
            </h2>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="card-elevated">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-primary mb-3 text-lg">{faq.question}</h3>
                    <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Warum renteplusimmobilie.de */}
      <section className="py-16 md:py-24 bg-accent/5">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto text-center">
            {/* Founder Image */}
            <div className="mb-8">
              <img 
                src={janineHardiImage} 
                alt="Janine Hardi - Gründerin von RentePlusImmobilie" 
                className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover mx-auto shadow-lg"
              />
              <p className="text-sm text-muted-foreground mt-3">Janine Hardi, Gründerin von RentePlusImmobilie</p>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
              Warum RentePlusImmobilie
            </h2>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="h-8 w-8 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Unabhängige Beratung</h3>
                      <p className="text-muted-foreground">Vergleich aller Modelle, nicht nur Kredite.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Euro className="h-8 w-8 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Kostenlos & unverbindlich</h3>
                      <p className="text-muted-foreground">Keine versteckten Gebühren.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="h-8 w-8 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Spezialisten für Seniorenfinanzierung</h3>
                      <p className="text-muted-foreground">Erfahrung mit Banken und Investoren.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Users className="h-8 w-8 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Familienorientierte Lösungen</h3>
                      <p className="text-muted-foreground">Eigentum bleibt in der Familie, während Liquidität entsteht.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Fazit & CTA */}
      <section className="py-16 md:py-24">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8">
              Fazit – Seniorenfinanzierung als diskrete und sichere Lösung
            </h2>
            
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Die Seniorenfinanzierung bietet eine diskrete Möglichkeit, Kapital aus der Immobilie zu lösen, ohne diese verkaufen zu müssen.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <CreditCard className="h-8 w-8 text-accent mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Flexible Auszahlung je nach Bedarf.</p>
                </CardContent>
              </Card>
              
              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <Clock className="h-8 w-8 text-accent mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Keine monatliche Tilgung zu Lebzeiten.</p>
                </CardContent>
              </Card>
              
              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <Users className="h-8 w-8 text-accent mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Rückzahlung erst nach dem Tod oder durch die Erben.</p>
                </CardContent>
              </Card>
              
              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <Home className="h-8 w-8 text-accent mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Eigentum bleibt erhalten.</p>
                </CardContent>
              </Card>
            </div>

            <p className="text-lg text-muted-foreground mb-12 leading-relaxed">
              Mit der kostenlosen Beratung von renteplusimmobilie.de können Eigentümer prüfen, ob eine Seniorenfinanzierung die richtige Lösung ist – oder ob Alternativen wie Leibrente, Teilverkauf oder Rückmiete besser passen.
            </p>

            <div className="bg-accent/10 p-8 rounded-xl mb-12">
              <p className="text-lg mb-4">
                👉 <strong>Wer Kapital benötigt und gleichzeitig Eigentum und Sicherheit bewahren möchte, sollte die Seniorenfinanzierung prüfen.</strong>
              </p>
            </div>

            <Button size="lg" className="text-xl px-12 py-6" onClick={() => window.open('https://calendly.com/renteplusimmobilie/20min', '_blank')}>
              <Calculator className="mr-2 h-6 w-6" />
              Jetzt kostenlose Beratung anfragen
            </Button>
          </div>
        </div>
      </section>

      {/* Link to Erfolgsgeschichten */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-accent/10 via-background to-primary/5">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4">
              Echte Geschichten, echte Lösungen
            </h2>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Lernen Sie vier Paare kennen, die unterschiedliche Wege gefunden haben, ihr Kapital freizusetzen – ohne ihr Zuhause zu verlieren.
            </p>
            <Link to="/erfolgsgeschichten">
              <Button size="lg" variant="outline" className="text-lg px-8">
                Erfolgsgeschichten lesen
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      <ContactSection />
      <HeyflowBanner />
      </main>
    </div>
  );
};

export default Seniorenfinanzierung;